// @ts-strict-ignore
import { Type } from '@avro/types/lib'
import { ClientAuditLogEntry } from 'api/audit/types'
import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { Buffer } from 'buffer'
import { decodeAvro, encodeAvro } from 'utils/avro'
import { fetchGraphQLQuery } from './graphQL/requests'
import { Graph } from './graphs/types'

export const nodeIdType = Type.forSchema<{ model: string; key: Uint8Array }>({
  name: 'NodeId',
  type: 'record',
  fields: [
    { name: 'model', type: 'string' },
    { name: 'key', type: 'bytes' },
  ],
})

export function encodeNodeId<T>(data: T, encoder: Type<T>) {
  const encoded = encodeAvro(data, encoder)
  return encodeAvro(
    {
      key: Uint8Array.from(Buffer.from(encoded, 'base64')),
      model: encoder.name.replace('__Key', ''),
    },
    nodeIdType
  )
}

export function decodeNodeId<T>(id: string, decoder: Type<T>) {
  try {
    const nodeId = decodeAvro(id, nodeIdType)

    if (`${nodeId.model}__Key` !== decoder.name) {
      throw new Error(
        `Type ${decoder.name} cannot be used to decode id for model ${nodeId.model}`
      )
    }

    return decodeAvro(Buffer.from(nodeId?.key), decoder)
  } catch (error) {
    throw new Error(
      `Cannot decode node id [ ${id} ]: ${(error as Error).message}'`
    )
  }
}

export function isMaintenanceEnabled(res: AxiosResponse) {
  return res.headers['x-faros-maintenance'] === '1'
}

/**
 * `toPostgresArray` converts classic array to Postgres array literal
 * @example [foo, bar] => '{foo, bar}'
 * */
export const toPostgresArray = (array: Array<string>) => `{${array.join(', ')}}`

type FetchPaginatedGraphQLQueryProps<TResponse, TVariables, TData> = {
  limit?: number
  query: string
  graph: Graph
  variables?: Omit<TVariables, 'limit' | 'offset'>
  getData?: (response: TResponse) => Array<TData> | Record<string, Array<TData>>
  config?: AxiosRequestConfig & { auditLogEntry?: ClientAuditLogEntry }
}

/**
 * Fetches a paginated GraphQL query. Look at useTeamChainData.ts for an example of usage.
 *
 *
 * @param {Object} params - parameters are all passed in as properties of an object.
 * @param {Graph} params.graph - The graph object to use for the query.
 * @param {string} params.query - The GraphQL query to execute.
 * @param {Object} [params.variables] - The variables to include in the query, excluding 'limit' and 'offset'. These are populated by defaults.
 * @param {Function} [params.getData] - A function to extract the data array from the response.
 *
 * @returns {Promise<Array<TData> | Record<string, Array<TData>>>} A promise that resolves to an array of data from the query.
 *
 * @template TResponse The expected response type from the GraphQL query.
 * @template TVariables The expected variables type for the GraphQL query.
 * @template TData The type of the data items in the response.
 */
export const fetchPaginatedGraphQLQuery = async <
  TResponse,
  TVariables,
  TData = unknown,
>({
  variables,
  query,
  graph,
  getData,
  config,
}: FetchPaginatedGraphQLQueryProps<TResponse, TVariables, TData>) => {
  const limit = 1000
  let response: TResponse | null = null
  let data: Array<TData> | Record<string, Array<TData>> = null
  let currentOffset = 0
  let responseMatchesLimit = null

  do {
    responseMatchesLimit = false

    // eslint-disable-next-line no-await-in-loop
    response = await fetchGraphQLQuery<TResponse>(
      graph,
      query,
      {
        limit,
        offset: currentOffset,
        ...variables,
      },
      config
    )

    const parsedResponse = getData(response)
    if (Array.isArray(parsedResponse)) {
      if (!data) {
        data = []
      }

      data = (data as Array<TData>).concat(parsedResponse)

      if (parsedResponse.length === limit) {
        responseMatchesLimit = true
      }
    } else {
      if (!data) {
        data = {}
      }

      // eslint-disable-next-line @typescript-eslint/no-loop-func
      Object.keys(parsedResponse).forEach((key) => {
        if (!data[key]) {
          data[key] = parsedResponse[key]
        } else {
          data[key] = (data[key] as Array<TData>).concat(parsedResponse[key])
        }

        if (parsedResponse[key].length === limit) {
          responseMatchesLimit = true
        }
      })
    }

    currentOffset += limit
  } while (responseMatchesLimit)

  return data
}
